import { useState, useCallback, useRef } from 'react';
import { Alert, Flex, Button, Typography } from 'antd';
import Icon, {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EllipsisOutlined,
  LoadingOutlined,
} from '@ant-design/icons';

import SectionMessage from 'components/SectionMessage/SectionMessage';
import { SIZE, STATUS } from 'utilities/constants';

const { Text } = Typography;

/**
 * Custom hook for managing submit progress UI across different components
 *
 * @param {Object} config - Configuration object
 * @param {Array} config.steps - Array of step configurations
 * @param {Function} config.onSubmit - Component-specific submit logic function
 * @param {string} config.successMessage - Message to display on successful completion
 * @param {string} config.successTestId - Test ID for success message
 * @param {Object} config.containerStyles - Optional styles for the container
 * @param {Function} config.onComplete - Callback when all steps complete successfully
 * @param {Function} config.onError - Callback when an error occurs
 * @param {boolean} config.showSuccessOverlay - Whether to show success overlay (default: true)
 *
 * Step configuration:
 * @param {Array} step.queries - Array of Apollo query/mutation objects to track
 * @param {string} step.text - Display text for the step
 * @param {boolean} step.disabled - Whether this step's retry is disabled
 * @param {string} step.errorMessage - Custom error message for this step
 * @param {string} step.errorTestId - Test ID for error alert
 *
 * @returns {Object} - Object containing render functions, state, and handleSubmit function
 */
export const useSubmitProgress = (config) => {
  const {
    steps = [],
    onSubmit,
    successMessage = 'Operation completed successfully',
    successTestId = 'submit-success-message',
    containerStyles = {},
    onComplete,
    showSuccessOverlay = true,
  } = config;

  const [submissionCompleted, setSubmissionCompleted] = useState(false);
  const [submissionInitiated, setSubmissionInitiated] = useState(false);
  const hasSubmittedRef = useRef(false);

  const reset = () => {
    setSubmissionCompleted(false);
    setSubmissionInitiated(false);
    hasSubmittedRef.current = false;
  };

  const markCompleted = useCallback(() => {
    setSubmissionCompleted(true);
    if (onComplete) {
      onComplete({ reset });
    }
  }, [onComplete]);

  const markInitiated = () => {
    setSubmissionInitiated(true);
  };

  const handleSubmit = useCallback(async () => {
    try {
      const isRetry = submissionInitiated && hasSubmittedRef.current;

      if (!isRetry && (submissionInitiated || hasSubmittedRef.current)) {
        return;
      }

      if (!isRetry) {
        hasSubmittedRef.current = true;
      }

      if (onSubmit) {
        await onSubmit({
          markInitiated,
          markCompleted,
          submissionInitiated,
          isRetry,
          hasSubmittedRef,
          reset,
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      throw error;
    }
  }, [onSubmit, markInitiated, markCompleted, submissionInitiated]);

  const stepsWithHandleSubmit = steps.map((step) => ({
    ...step,
    handleSubmit,
  }));

  const renderStepProgress = (step, stepIndex) => {
    const { queries = [], text, handleSubmit, disabled = false } = step;

    let icon = EllipsisOutlined;
    let color = 'var(--color-text-secondary)';

    const complete = queries.every((query) => query?.data);
    const loading = queries.some((query) => query?.loading);
    const error = queries.some((query) => query?.error);

    if (loading) {
      icon = LoadingOutlined;
      color = 'var(--color-text)';
    } else if (error) {
      icon = CloseCircleOutlined;
      color = 'var(--color-error)';
    } else if (complete) {
      icon = CheckCircleOutlined;
      color = 'var(--color-success)';
    }

    return (
      <Flex gap="var(--spacing)" align="center" key={`step-${stepIndex}`}>
        <Icon
          component={icon}
          style={{ color, fontSize: 'var(--font-size-lg)' }}
        />
        <Text size="large" style={{ color, fontSize: 'var(--font-size-lg)' }}>
          {text}
        </Text>
        {error && handleSubmit ? (
          <Button
            data-testid="retry-step-button"
            onClick={() => {
              handleSubmit();
            }}
            disabled={disabled}
          >
            Retry Step
          </Button>
        ) : null}
      </Flex>
    );
  };

  const renderErrorAlerts = () => {
    return stepsWithHandleSubmit.map((step, index) => {
      const { errorMessage, errorTestId, queries = [] } = step;

      if (!errorMessage) return null;

      const hasError = queries.some((query) => query?.error);

      if (!hasError) return null;

      return (
        <Alert
          key={`error-${index}`}
          data-testid={errorTestId}
          type="error"
          banner
          title={errorMessage}
        />
      );
    });
  };

  const renderProgress = useCallback(() => {
    const defaultContainerStyles = {
      paddingTop: 'var(--spacing-md)',
      paddingBottom: 'var(--spacing-md)',
      height: '300px',
    };

    return (
      <div style={{ position: 'relative' }}>
        {submissionCompleted && showSuccessOverlay && (
          <SectionMessage
            data-testid={successTestId}
            cover={true}
            status={STATUS.SUCCESS}
            size={SIZE.SM}
            text={successMessage}
          />
        )}
        <Flex
          vertical={true}
          align="center"
          justify="center"
          gap="var(--spacing)"
          style={{
            ...defaultContainerStyles,
            ...containerStyles,
          }}
        >
          {stepsWithHandleSubmit.map((step, index) =>
            renderStepProgress(step, index),
          )}
          {renderErrorAlerts()}
        </Flex>
      </div>
    );
  }, [
    submissionCompleted,
    showSuccessOverlay,
    successTestId,
    successMessage,
    containerStyles,
    stepsWithHandleSubmit,
    renderStepProgress,
    renderErrorAlerts,
  ]);

  return {
    // State
    submissionCompleted,
    submissionInitiated,

    // Actions
    handleSubmit,
    markCompleted,
    markInitiated,
    reset,

    // Render functions
    renderProgress,
    renderStepProgress,
  };
};

export default useSubmitProgress;
